import endPoints from '../api/endpoint';
import type { ManageEntityAccessMemberFlags } from './manageEntityMember';

export type ManageEntitiesAccessUserType = 'CONTACT' | 'CREW_MEMBER';

export type ManageEntitiesAccessSortType = 'dept' | 'name' | 'access';

/** Internal key when API returns a flat `members` array (name / access sort). */
export const MANAGE_ENTITY_FLAT_MEMBERS_KEY = '__flat_members__';

export interface ManageEntityAccessMember extends ManageEntityAccessMemberFlags {
  isOwnerFromDoc?: boolean;
  firstName?: string;
  lastName?: string;
  profilePicture?: string;
  roleName?: string;
  departmentName?: string | null;
  departmentId?: string | null;
}

export interface ManageEntityMemberGroup {
  department: string;
  members: ManageEntityAccessMember[];
  hideDepartmentHeading?: boolean;
}

export interface NormalizedManageEntitiesAccessData {
  userPermissions: Record<string, ManageEntityAccessMember[]>;
  members?: ManageEntityAccessMember[];
  total?: number;
  page?: number;
  limit?: number;
  isFlatList: boolean;
  memberGroups: ManageEntityMemberGroup[];
}

const emptyNormalizedAccess = (): NormalizedManageEntitiesAccessData => ({
  userPermissions: {},
  isFlatList: false,
  memberGroups: [],
});

export const normalizeManageEntitiesAccessData = (
  data: Record<string, unknown> | null | undefined,
): NormalizedManageEntitiesAccessData => {
  if (!data) return emptyNormalizedAccess();

  const flatMembers = data.members;
  if (Array.isArray(flatMembers)) {
    const members = flatMembers as ManageEntityAccessMember[];
    return {
      userPermissions: { [MANAGE_ENTITY_FLAT_MEMBERS_KEY]: members },
      members,
      total: typeof data.total === 'number' ? data.total : undefined,
      page: typeof data.page === 'number' ? data.page : undefined,
      limit: typeof data.limit === 'number' ? data.limit : undefined,
      isFlatList: true,
      memberGroups: [
        {
          department: MANAGE_ENTITY_FLAT_MEMBERS_KEY,
          members,
          hideDepartmentHeading: true,
        },
      ],
    };
  }

  const userPermissions = data.userPermissions as
    | Record<string, ManageEntityAccessMember[]>
    | undefined;

  if (!userPermissions || typeof userPermissions !== 'object') {
    return emptyNormalizedAccess();
  }

  const memberGroups = Object.entries(userPermissions)
    .filter(([, members]) => Array.isArray(members) && members.length > 0)
    .map(([department, members]) => ({
      department,
      members,
      hideDepartmentHeading: false,
    }));

  return {
    userPermissions,
    isFlatList: false,
    memberGroups,
    total: typeof data.total === 'number' ? data.total : undefined,
  };
};

/** Stable id for chat member pickers (API may send `userId` and/or `_id`). */
export const getChatMemberId = (member: ManageEntityAccessMember): string =>
  String(member.userId || (member as { _id?: string })._id || '');

/**
 * Normalizes chat entity member list API response:
 * `[{ departmentName, members }, { owners }]`, or legacy flat member array.
 */
export const normalizeChatEntityMembersList = (
  data: unknown,
): ManageEntityMemberGroup[] => {
  if (!data) return [];


  if (!Array.isArray(data)) return [];

  const ownerGroups: ManageEntityMemberGroup[] = [];
  const departmentGroups: ManageEntityMemberGroup[] = [];

  for (const item of data) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;

    if (Array.isArray(row.owners)) {
      const owners = row.owners as ManageEntityAccessMember[];
      if (owners.length > 0) {
        ownerGroups.push({ department: 'Owners', members: owners });
      }
      continue;
    }

    if (Array.isArray(row.members)) {
      const members = row.members as ManageEntityAccessMember[];
      if (members.length === 0) continue;
      departmentGroups.push({
        department: String(row.departmentName || 'Members').trim(),
        members,
      });
    }
  }

  return [...ownerGroups, ...departmentGroups];
};

export const findManageEntityAccessMember = (
  data: Record<string, unknown> | null | undefined,
  userId: string,
): ManageEntityAccessMember | null => {
  const normalized = normalizeManageEntitiesAccessData(data);
  if (normalized.members?.length) {
    return normalized.members.find(m => m.userId === userId) ?? null;
  }
  for (const members of Object.values(normalized.userPermissions)) {
    if (!Array.isArray(members)) continue;
    const found = members.find(m => m.userId === userId);
    if (found) return found;
  }
  return null;
};

export const buildManageEntitiesAccessEndpoint = (
  entityType: string,
  entityId: string,
  userType: ManageEntitiesAccessUserType,
  searchKey?: string,
  sortType: ManageEntitiesAccessSortType = 'dept',
): string => {
  const params = new URLSearchParams({
    entityType,
    entityId,
    sortType,
    userType,
  });

  if (searchKey) {
    params.set('searchKey', searchKey);
  }

  return `${endPoints.manageEntitiesAccess}?${params.toString()}`;
};

export const resolveManageEntitiesAccessTarget = (
  entityType: string | undefined,
  routeId: string | undefined,
  episodeId?: string | null,
  setId?: string | null,
  locationId?: string | null,
): { entityType: string; entityId: string } | null => {
  if (!entityType || !routeId) return null;

  if (entityType === 'productions') {
    return { entityType, entityId: routeId };
  }
  if (entityType === 'episodes' && episodeId) {
    return { entityType, entityId: episodeId };
  }
  if (entityType === 'sets' && setId) {
    return { entityType, entityId: setId };
  }
  if (entityType === 'locations' && locationId) {
    return { entityType, entityId: locationId };
  }
  if (entityType === 'studios') {
    return { entityType, entityId: routeId };
  }

  return null;
};
